/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import createAuth0Client, { Auth0ClientOptions } from '@auth0/auth0-spa-js';
import get from 'lodash/get';
import qs from 'query-string';
import React, { useContext, useEffect, useState } from 'react';

import {
  clearCallbackRetry,
  getPrivateGroups,
  getPublicGroups,
  handleCallbackFailure,
  hasAccess,
  IAuth0ProviderOptionsBase,
  IAuth0QueryParamsBase,
  isAuthz,
  mapAuthzScopes,
  mapRoleGroups,
  refreshAccessToken,
  SessionStorage,
  startLogin,
  startTokenRefresh,
  trackException,
} from '@marapp/earth-shared';

import auth0 from '../config/auth0';
import { routeToPage } from '../utils';
import { Auth0Context } from '../utils/contexts';
import { MAP_PUBLIC_ORG } from '../config';

// Auth0 will enforce namespacing when performing OIDC-conformant
// login flows, meaning that any custom claims without HTTP/HTTPS
// namespaces will be silently excluded from tokens.
const NAMESPACE = auth0.config.namespace;
const PERSISTED_GROUPS_KEY = 'ephemeral-groups';

// Workspace state belongs to the session being replaced, not the one being
// started. Passed to startLogin rather than run before it so it only happens
// on the call that actually redirects.
const clearSessionState = () => {
  SessionStorage.remove('ephemeral');
  SessionStorage.remove(PERSISTED_GROUPS_KEY);
};

export const useAuth0 = () => useContext(Auth0Context);

interface Auth0ProviderOptions extends IAuth0ProviderOptionsBase {
  onRedirectCallback(params: { targetUrl?: string }): boolean;
  onFailureHook(params: any): boolean;
}

interface IQueryParams extends IAuth0QueryParamsBase {
  loggedOut?: string;
}

export const Auth0Provider = ({
  children,
  onRedirectCallback,
  onSuccessHook,
  onFailureHook,
  ...initOptions
}: Auth0ProviderOptions & Auth0ClientOptions) => {
  const [client, setClient] = useState<any>({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [email, setEmail] = useState('');
  const [userData, setUserData] = useState({});

  const [groups, setGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [privateGroups, setPrivateGroups] = useState([]);
  const [roles, setRoles] = useState({});
  const [permissions, setPermissions] = useState({});
  // TODO: rename this to selectedGroups
  const [selectedGroup, setSelectedGroup] = useState([]);

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions as any);
      const queryParams: IQueryParams = qs.parse(window.location.search);
      setClient(auth0FromHook);

      // A sign-out in earth-admin lands here with this flag. Its logout cleared
      // the shared SSO session, and the token cache too where both apps sit on
      // one origin - but not in local dev, where admin runs on another port and
      // so another localstorage. Drop the token ourselves, then strip the flag
      // so a reload doesn't repeat it, and bootstrap on as signed out.
      if (queryParams.loggedOut) {
        await auth0FromHook.logout({ localOnly: true });
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      // Auth0's email-verification page returns to the app with code=success and
      // no state. That is not an authorization code - a real OAuth callback
      // always carries both - and must not be fed to handleRedirectCallback.
      const isEmailVerificationReturn = queryParams.code === 'success' && !queryParams.state;

      if (queryParams.code && queryParams.state) {
        try {
          const { appState } = await auth0FromHook.handleRedirectCallback();
          clearCallbackRetry();
          if (!onRedirectCallback({ targetUrl: appState?.targetUrl })) {
            return;
          }
        } catch (e: any) {
          // hold the spinner while a retry redirect is on its way, and fall
          // through to the routing below when there is none
          const retrying = await handleCallbackFailure(e, auth0FromHook, {
            onBeforeRedirect: clearSessionState,
          });

          if (retrying) {
            return;
          }
          // A false return means a full page load is pending, so stop here.
          // Flipping isLoading would render the current route first, and an
          // auth-required one calls login() - a redirect that would beat the
          // navigation just queued and send the user back to Auth0.
          if (!routeToPage({})) {
            return;
          }
        }
      }

      if (queryParams.error && !onFailureHook(queryParams)) {
        return;
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();

      // The cached token predates the verification, and email_verified only
      // changes on a freshly issued one. Bypassing the cache also re-caches the
      // ID token that getUser() reads below.
      const getAccessToken = async () => {
        if (!isEmailVerificationReturn) {
          return auth0FromHook.getTokenSilently();
        }
        try {
          return await auth0FromHook.getTokenSilently({ ignoreCache: true });
        } catch (e: any) {
          // A forced refresh can fail where a cached read wouldn't (rotated
          // refresh token, blocked iframe). Fall back to the cached token so a
          // still-valid session isn't rendered as signed out.
          trackException(e, { source: 'auth0', stage: 'getTokenSilently' });
          return auth0FromHook.getTokenSilently();
        }
      };

      const accessToken = isAuthenticated ? await getAccessToken() : null;
      onSuccessHook({ accessToken, authClient: auth0FromHook });
      setIsAuthenticated(isAuthenticated);

      const idToken = accessToken ? await auth0FromHook.getUser() : {};

      const roles = get(idToken, `${NAMESPACE}/roles`, []);
      const mappedRoles = mapAuthzScopes(roles);
      setRoles(mappedRoles);

      setPrivateGroups(getPrivateGroups(mappedRoles));
      setPublicGroups(getPublicGroups(mappedRoles));

      const roleGroups = mapRoleGroups(roles, ['*']); // exclude special groups;
      setGroups(roleGroups);

      const permissions = get(idToken, `${NAMESPACE}/permissions`, []);
      setPermissions(mapAuthzScopes(permissions));

      const emailVerified = get(idToken, 'email_verified', false);
      setIsEmailVerified(emailVerified);

      const authorized = isAuthz(roles);
      setIsAuthorized(authorized);

      const givenName = get(idToken, 'given_name', '');
      const familyName = get(idToken, 'family_name', '');

      const userData = {
        email: get(idToken, 'email', ''),
        name:
          givenName || familyName ? `${givenName} ${familyName}`.trim() : get(idToken, 'name', ''),
        picture: get(idToken, 'picture', ''),
        allGroups: roleGroups,
        roles: mappedRoles,
      };
      setUserData(userData);
      setEmail(userData.email);

      const persistedGroups = SessionStorage.getObject(PERSISTED_GROUPS_KEY, null);

      // only select orgs that are available in the token
      const selected = persistedGroups
        ? roleGroups.filter((g) => persistedGroups.includes(g))
        : roleGroups;

      setSelectedGroup(selected);

      setIsLoading(false);
    };
    initAuth0().catch((e) => {
      // nothing awaits this, so an escape here would only surface as an
      // unhandled rejection and leave the app stuck on the loading screen
      trackException(e, { source: 'auth0', stage: 'initAuth0' });
      setIsLoading(false);
    });
  }, []); // eslint-disable-line

  // Renew the access token before it expires, so a long-lived session keeps
  // working without the user having to reload the page
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    return startTokenRefresh({
      onSessionExpired: () => {
        // fall back to the signed-out experience rather than leaving the UI
        // looking authenticated against a session that no longer exists.
        // Workspace state has to go too - anything gated on permissions or
        // privateGroups would otherwise keep offering affordances that 401.
        setIsAuthenticated(false);
        setIsAuthorized(false);
        setIsEmailVerified(false);
        setEmail('');
        setUserData({});
        setGroups([]);
        setPublicGroups([]);
        setPrivateGroups([]);
        setRoles({});
        setPermissions({});
        setSelectedGroup([]);

        // 'ephemeral' holds map state rather than anything authenticated, and
        // the store would write it straight back anyway
        SessionStorage.remove(PERSISTED_GROUPS_KEY);
      },
    });
  }, [isAuthenticated]);

  // Persist user selected groups
  // When all groups are selected, persist none
  // Skipped without a session, so clearing the selection on expiry sticks
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const allSelected = selectedGroup.length === groups.length;
      const persistedGroups = allSelected ? [] : selectedGroup;
      SessionStorage.add(PERSISTED_GROUPS_KEY, JSON.stringify(persistedGroups));
    }
  }, [selectedGroup, isLoading, isAuthenticated]);

  /**
   * Logging-in will automatically request the offline_access scope and store the resulting
   * refresh token.
   * @param options
   */
  // A client that isn't ready yet is a no-op rather than a TypeError, so the
  // callers that neither await nor catch are safe on that path. A redirect that
  // rejects still propagates.
  const login = (options = {}) =>
    startLogin(client, { options, onBeforeRedirect: clearSessionState });

  const logout = (options = {}) => {
    clearSessionState();

    // force the user to log out of their identity provider;
    return client.logout({ ...options, federated: true });
  };

  const getUser = (options = {}) => {
    return client.getUser(options);
  };

  const updateToken = async () => {
    // callers use this to pick up claims that just changed server-side
    if (isAuthenticated) {
      await refreshAccessToken({ force: true });
    }
  };

  const getPermissions = (type: string[] | string[][], org: string) => {
    return hasAccess(permissions[org], type) || hasAccess(permissions['*'], type);
  };

  const togglePrivateWorkspaces = () => {
    const somePrivateGroupsAreDeselected =
      selectedGroup.length > 0 &&
      selectedGroup.filter((sg) => sg !== MAP_PUBLIC_ORG).length !==
        groups.filter((g) => g !== MAP_PUBLIC_ORG).length;

    setSelectedGroup(
      somePrivateGroupsAreDeselected
        ? selectedGroup.includes(MAP_PUBLIC_ORG)
          ? groups
          : groups.filter((g) => g !== MAP_PUBLIC_ORG)
        : [MAP_PUBLIC_ORG]
    );
  };

  return (
    <Auth0Context.Provider
      value={{
        isLoading,
        isAuthenticated,
        isAuthorized,
        isEmailVerified,
        email,
        userData,
        groups,
        publicGroups,
        privateGroups,
        roles,
        permissions,
        selectedGroup: selectedGroup.length ? selectedGroup : groups,
        login,
        logout,
        getUser,
        updateToken,
        getPermissions,
        setupUserOrg: setSelectedGroup,
        togglePrivateWorkspaces,
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
